import type { ContextType, Ref } from 'preact';
import { forwardRef } from 'preact/compat';

import type { StyledComponentProps } from '@oracle/oraclejet-internal-utilities/typeUtils';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import { useInputGroupContext } from '#hooks/UNSAFE_useInputGroupContext';
import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { SkeletonRedwoodTheme } from '#UNSAFE_Skeleton/themes/redwood/SkeletonTheme';
import { isEmbeddedVariant } from '#utils/PRIVATE_formControlUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { type LayoutColumnSpan, layoutSpanStyles } from '#utils/UNSAFE_styles/Layout';
import { FormLayoutRedwoodTheme } from './themes/redwood/FormLayoutTheme';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';
import type { TextFieldStyles, TextFieldVariantOptions } from './themes/TextFieldStyles.css';

export type StyleProps = {
  columnSpan?: LayoutColumnSpan;
  variant?: ContextType<typeof FormVariantContext>;
};
type Props = StyledComponentProps<'div', StyleProps>;

/**
 * StyledTextField is the main root container for all text field layouts.
 * It renders a <div> with OracleJET theme-based classes, applying styles
 * and layout variants for text fields. This component does not handle any
 * business logic or validation, but ensures correct styling is applied
 * based on props such as columnSpan, variant, input group context, readonly/embedded modes, etc.
 * All event handlers, refs, and children are passed through to the div.
 */
export const StyledTextField = forwardRef(
  ({ columnSpan = 1, variant = 'default', ...props }: Props, ref?: Ref<HTMLElement>) => {
    // because of the mismatch of height for inputs with inside labels and no labels,
    // we'll have to set everything to the same min-height while within an input group context.
    // Here we'll look to see if it's present and apply the style as needed
    const groupContext = useInputGroupContext();
    const { isReadonly } = useFormFieldContext();
    const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);
    const { baseTheme: labelBaseTheme } = useComponentTheme(LabelRedwoodTheme);
    const { classes: textFieldClasses, styles } = useComponentTheme<
      TextFieldVariantOptions,
      TextFieldStyles
    >(TextFieldRedwoodTheme, {
      readonly: isReadonly ? 'isReadonly' : 'notReadonly',
      withinGroup: groupContext !== null ? 'isWithinGroup' : undefined
    });

    const { formControlBase, textFieldBase, textFieldEmbedded } = styles;

    // because TextField components reference Skeleton scoped vars, we need to bring in the Skeleton base theme
    // to pick up the scoped var definitions.
    const { baseTheme: skeletonBaseTheme } = useComponentTheme(SkeletonRedwoodTheme);

    const classes = classNames([
      skeletonBaseTheme,
      formControlBase,
      textFieldBase,
      isEmbeddedVariant(variant) && textFieldEmbedded,
      textFieldClasses,
      formLayoutTheme,
      labelBaseTheme,
      layoutSpanStyles.layoutSpanColumn[columnSpan]
    ]);

    return <div {...props} class={classes} ref={ref as Ref<HTMLDivElement> | undefined} />;
  }
);
